import React from "react"
import { useParams, Link } from "react-router-dom"
import {
  Card,
  CardBody,
  CardTitle,
  Col,
  Container,
  Row,
  Table,
} from "reactstrap"
import Breadcrumbs from "components/Common/Breadcrumb"
import { useShowroomBySlug } from "hooks/showroomHook"
import profilePlaceholder from "assets/images/users/avatar-1.jpg"
import coverPlaceholder from "assets/images/profile-img.png"
import ShowroomContactUpdate from "./ShowroomContactUpdate"
import ShowroomSocialUpdate from "./ShowroomSocialUpdate"
import ShowroomDetailsUpdate from "./ShowroomDetailsUpdate"
import StatusBadge from "components/Common/StatusBadge"
import { formatReadableDate, formatReadableTime } from "utils/formatDate"
import ShowroomProfileUpdate from "./ShowroomProfileUpdate"

const ShowroomProfile = () => {
  const { slug } = useParams()
  const { data, isLoading } = useShowroomBySlug(slug)
  const showroom = data?.data || {}

  document.title = `Showroom Profile${
    showroom.name ? ` | ${showroom.name}` : ""
  }`

  if (isLoading) return <div>Loading showroom profile...</div>

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs title="Showrooms" breadcrumbItem="Profile" />

        <Row>
          {/* Left Column: Profile Card */}
          <Col xl="4">
            <Card className="overflow-hidden">
              <div
                className="profile-cover position-relative"
                style={{
                  backgroundImage: `url(${
                    showroom.cover_image || coverPlaceholder
                  })`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  height: "150px",
                }}
              >
                <div
                  className="d-flex justify-content-center align-items-end"
                  style={{ height: "100%" }}
                >
                  <img
                    src={showroom.logo || profilePlaceholder}
                    alt="Showroom Logo"
                    className="rounded-circle img-thumbnail"
                    style={{
                      width: "100px",
                      height: "100px",
                      marginBottom: "-50px",
                      border: "3px solid #fff",
                      backgroundColor: "#fff",
                    }}
                  />
                </div>
              </div>
              <CardBody className="text-center mt-5">
                <h5 className="font-size-15">{showroom.name}</h5>
                <p className="text-muted mb-0">{showroom.short_name}</p>
                <p className="text-muted mb-0">{showroom.address}</p>
                <ShowroomProfileUpdate showroom={showroom} />
              </CardBody>
            </Card>

            {/* Contact Info */}
            <Card className="mt-3">
              <CardBody>
                <CardTitle className="mb-3">Contact Information</CardTitle>
                <Table borderless>
                  <tbody>
                    <tr>
                      <th>Mobile:</th>
                      <td>{showroom.mobile || "-"}</td>
                    </tr>
                    <tr>
                      <th>Alt Mobile:</th>
                      <td>{showroom.alt_mobile || "-"}</td>
                    </tr>
                    <tr>
                      <th>Email:</th>
                      <td>{showroom.email || "-"}</td>
                    </tr>
                    <tr>
                      <th>Website:</th>
                      <td>
                        {showroom.website ? (
                          <a
                            href={showroom.website}
                            target="_blank"
                            rel="noreferrer"
                          >
                            {showroom.website}
                          </a>
                        ) : (
                          "-"
                        )}
                      </td>
                    </tr>
                    <tr>
                      <th>Google Map:</th>
                      <td>
                        {showroom.google_map ? (
                          <a
                            href={showroom.google_map}
                            target="_blank"
                            rel="noreferrer"
                          >
                            View Map
                          </a>
                        ) : (
                          "-"
                        )}
                      </td>
                    </tr>
                  </tbody>
                </Table>
                <ShowroomContactUpdate showroom={showroom} />
              </CardBody>
            </Card>

            {/* Social Links */}
            <Card className="mt-3">
              <CardBody>
                <CardTitle className="mb-3">Social Links</CardTitle>
                <Table borderless>
                  <tbody>
                    <tr>
                      <th>Facebook:</th>
                      <td>{showroom.facebook || "-"}</td>
                    </tr>
                    <tr>
                      <th>Instagram:</th>
                      <td>{showroom.instagram || "-"}</td>
                    </tr>
                    <tr>
                      <th>YouTube:</th>
                      <td>{showroom.youtube || "-"}</td>
                    </tr>
                    <tr>
                      <th>TikTok:</th>
                      <td>{showroom.tiktok || "-"}</td>
                    </tr>
                    <tr>
                      <th>LinkedIn:</th>
                      <td>{showroom.linkedin || "-"}</td>
                    </tr>
                  </tbody>
                </Table>
                <ShowroomSocialUpdate showroom={showroom} />
              </CardBody>
            </Card>
          </Col>

          {/* Right Column: Showroom Details */}
          <Col xl="8">
            <Card>
              <CardBody>
                <CardTitle className="mb-3">Showroom Details</CardTitle>
                <Table borderless>
                  <tbody>
                    <tr>
                      <th>Capacity:</th>
                      <td>
                        {showroom.capacity ? `${showroom.capacity} cars` : "-"}
                      </td>
                    </tr>
                    <tr>
                      <th>Service Center:</th>
                      <td>
                        <StatusBadge
                          value={showroom.has_service_center}
                          trueLabel="Yes"
                          falseLabel="No"
                        />
                      </td>
                    </tr>
                    <tr>
                      <th>Parking Available:</th>
                      <td>
                        <StatusBadge
                          value={showroom.has_parking}
                          trueLabel="Yes"
                          falseLabel="No"
                        />
                      </td>
                    </tr>
                    <tr>
                      <th>Opening Time:</th>
                      <td>
                        {showroom.opening_time
                          ? formatReadableTime(showroom.opening_time)
                          : "-"}
                      </td>
                    </tr>
                    <tr>
                      <th>Closing Time:</th>
                      <td>
                        {showroom.closing_time
                          ? formatReadableTime(showroom.closing_time)
                          : "-"}
                      </td>
                    </tr>
                    <tr>
                      <th>Latitude:</th>
                      <td>{showroom.latitude || "-"}</td>
                    </tr>
                    <tr>
                      <th>Longitude:</th>
                      <td>{showroom.longitude || "-"}</td>
                    </tr>
                    <tr>
                      <th>Description:</th>
                      <td>{showroom.description || "-"}</td>
                    </tr>
                    <tr>
                      <th>Status:</th>
                      <td>
                        <StatusBadge
                          value={showroom.is_active}
                          trueLabel="Active"
                          falseLabel="Inactive"
                        />
                      </td>
                    </tr>
                  </tbody>
                </Table>
                <ShowroomDetailsUpdate showroom={showroom} />
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  )
}

export default ShowroomProfile
